import {useCallback, useEffect, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {useNavigate, useParams, useSearchParams} from "react-router";
import {Button, Typography, Accordion, AccordionSummary, AccordionDetails} from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import AddIcon from "@mui/icons-material/Add";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import {useMutation} from "@tanstack/react-query";
import {useLocation} from "react-router-dom";
import {ROUTES} from "../../router/config";
import Modal from "../../components/Modal";
import {useApplicationStorage} from "../../hooks/useApplicationStorage";
import {useStudentsRequest} from "../../hooks/useStudentsRequest";
import {useToast} from "../../context/toast";
import axiosManager from "../../services/axiosManager";
import {IdentityForm} from "./components/IdentityForm";
import {EnrollmentForm} from "./components/EnrollmentForm";
import {BackgroundForm} from "./components/BackgroundForm";
import {GuardianAddressForm} from "./components/GuardianAddressForm";
import "./StudentsPage.css";

const SECTIONS = {
    IDENTITY: "identity",
    ENROLLMENT: "enrollment",
    BACKGROUND: "background",
    ADDRESS: "address",
};

const StudentsPage = () => {
    const {t} = useTranslation();
    const toast = useToast();
    const location = useLocation();
    const navigate = useNavigate();
    const {applicationId, childId} = useParams();
    const [searchParams] = useSearchParams();

    const isEdit = searchParams.has("edit");
    const isModalMultipleOpen = searchParams.has("modal-multiple");

    const {studentsData, updateStudent, removeStudent, getStudents} = useApplicationStorage();
    const studentsNumber = useMemo(() => (getStudents())?.length, [getStudents, location.pathname]);

    const {putStudentsRequest, isPendingStudentsRequest} = useStudentsRequest(applicationId);

    const {mutateAsync: removeStudentRequest, isPending: isRemovingStudent} = useMutation({
        mutationFn: () => axiosManager.delete$(`applications/${applicationId}/students/${childId}`).then((response) => response?.data),
        onError: () => {
            toast.error(t("errors.common"));
        },
        onSuccess: () => {
            removeStudent(childId);
            const studentsLength = (getStudents())?.length;
            if (studentsLength === 0) navigate(`/${applicationId}/${ROUTES.STUDENTS}`);
            else navigate(`/${applicationId}/${ROUTES.CONFIRMATION}`);
        },
    });

    const [studentData, setStudentData] = useState(studentsData.find((s) => (s.childId === childId) || undefined) || {});
    const [expanded, setExpanded] = useState(undefined);
    const [showModal, setShowModal] = useState(isModalMultipleOpen);
    const [showRemoveModal, setShowRemoveModal] = useState(false);

    const hasStudentCreated = !!childId;

    useEffect(() => {
        if (!isModalMultipleOpen) setShowModal(false);
        setStudentData(studentsData.find((s) => (s.childId === childId) || undefined) || {});
    }, [childId, studentsData, isModalMultipleOpen]);

    const pretitle = useMemo(() => {
        const studentOrder = studentData?.order;
        if (!studentOrder || studentOrder === 0) return "";
        if (studentOrder === 1) return t("students.pretitle.2");
        if (studentOrder === 2) return t("students.pretitle.3");
        return `${studentOrder + 1}${t("students.pretitle.th")}`;
    }, [studentData?.order, t]);

    const getFinishedSections = useCallback((dataToCheck) => ({
        [SECTIONS.IDENTITY]: !!dataToCheck.forename && !!dataToCheck.surname && !!dataToCheck.dateOfBirth && !!dataToCheck.genderTypeId,
        [SECTIONS.ENROLLMENT]: !!dataToCheck.desiredStartDate && !!dataToCheck.notionalYearGroupId,
        [SECTIONS.BACKGROUND]: !!dataToCheck.firstLanguage && !!dataToCheck.nationality && !!dataToCheck.countryOfResidence && !!dataToCheck.ethnicities,
        [SECTIONS.ADDRESS]: !!dataToCheck.relationshipTypeId && (dataToCheck.hasOtherAddress
            ? (!!dataToCheck.address?.country && !!dataToCheck.address?.addressLine1 && !!dataToCheck.address?.city && !!dataToCheck.address?.area && !!dataToCheck.address?.code
                && !!dataToCheck.responsibleGuardian.title && !!dataToCheck.responsibleGuardian.forename && !!dataToCheck.responsibleGuardian.surname && !!dataToCheck.responsibleGuardian.email && !!dataToCheck.responsibleGuardian.phone && !!dataToCheck.responsibleGuardian.relationshipTypeId)
            : true),
    }), []);
    const finishedSections = useMemo(() => getFinishedSections(studentData), [getFinishedSections, studentData]);
    const isSectionUnlocked = useMemo(() => ({
        [SECTIONS.IDENTITY]: true,
        [SECTIONS.ENROLLMENT]: finishedSections[SECTIONS.IDENTITY],
        [SECTIONS.BACKGROUND]: finishedSections[SECTIONS.ENROLLMENT],
        [SECTIONS.ADDRESS]: finishedSections[SECTIONS.BACKGROUND],
    }), [finishedSections]);

    useEffect(() => {
        if (!finishedSections[SECTIONS.IDENTITY]) setExpanded(SECTIONS.IDENTITY);
        else if (!finishedSections[SECTIONS.ENROLLMENT]) setExpanded(SECTIONS.ENROLLMENT);
        else if (!finishedSections[SECTIONS.BACKGROUND]) setExpanded(SECTIONS.BACKGROUND);
        else setExpanded(SECTIONS.ADDRESS);
    }, [finishedSections]);

    const handleAccordionChange = (panel) => (event, isExpanded) => {
        setExpanded(isExpanded ? panel : null);
    };

    const handleBack = () => {
        const students = getStudents();
        const previousStudent = (students || []).find((it) => it.order === ((studentData?.order ?? 1) - 1));

        if (previousStudent) {
            navigate(`/${applicationId}/${ROUTES.STUDENTS}/${previousStudent.childId}`);
        } else {
            navigate(isEdit ? `/${applicationId}/${ROUTES.CONFIRMATION}` : `/${applicationId}/${ROUTES.ADDRESS}`);
        }
    };

    const onFinishAccordion = async (formData, wereDataChanged) => {
        try {
            const bodyData = {
                ...(studentData || {}),
                ...formData,
                onlineRegistrationId: applicationId,
                order: studentData?.order || 0,
                childId: childId || undefined,
            };
            updateStudent(childId || undefined, bodyData);
            setStudentData(bodyData);

            const finishedSectionWithNewData = getFinishedSections(bodyData);
            const unfinishedSection = Object.entries(finishedSectionWithNewData).find(([, done]) => !done)?.[0];

            if (expanded === SECTIONS.ADDRESS && unfinishedSection) {
                setExpanded(unfinishedSection);
                toast.error(t("errors.requiredFields"));
                return;
            }

            if (wereDataChanged && isEdit) {
                await putStudentsRequest(bodyData);
            } else if (wereDataChanged && expanded === SECTIONS.ADDRESS) {
                const response = await putStudentsRequest(bodyData);

                removeStudent(undefined);
                updateStudent(response.childId, {...bodyData, childId: response.childId});
                navigate(`/${applicationId}/${ROUTES.STUDENTS}/${response.childId}?modal-multiple`, {replace: true});
            }

            if (expanded === SECTIONS.ADDRESS) {
                const isLastStudentOrder = studentData.order === studentsNumber - 1;

                if (unfinishedSection) {
                    setExpanded(unfinishedSection);
                    toast.error(t("errors.requiredFields"));
                } else if (!isLastStudentOrder && studentsNumber > 1) {
                    onProceedNext();
                } else {
                    setShowModal(true);
                }
            } else {
                const nextAccordionIdx = Math.min(Object.values(SECTIONS).length - 1, Object.values(SECTIONS).indexOf(expanded) + 1);
                const nextAccordion = Object.values(SECTIONS)[nextAccordionIdx];
                setExpanded(nextAccordion);
            }
        } catch {
            // fail silently
        }
    };

    const onModalClose = () => {
        if (childId) navigate(`/${applicationId}/${ROUTES.STUDENTS}/${childId}`, {replace: true});
        setShowModal(false);
    };

    const onAddAnotherStudent = () => {
        updateStudent(undefined, {forename: "", order: studentsNumber});
        navigate(`/${applicationId}/${ROUTES.STUDENTS}`);
    };

    const onProceedNext = () => {
        const students = getStudents();
        const nextStudent = (students || []).find((it) => it.order === ((studentData?.order ?? -1) + 1));

        if (nextStudent) {
            navigate(`/${applicationId}/${ROUTES.STUDENTS}/${nextStudent.childId}`);
        } else {
            navigate(`/${applicationId}/${ROUTES.CONFIRMATION}`);
        }
    };

    const onRemoveStudent = () => {
        if (childId) removeStudentRequest(childId);
        else {
            removeStudent(undefined);
            const students = getStudents();
            const lastStudent = [...students].pop();
            if (lastStudent) navigate(`/${applicationId}/${ROUTES.STUDENTS}/${lastStudent.childId}`);
        }
    };

    const renderAccordion = useCallback((sectionName, title) => (
        <Accordion
            expanded={expanded === sectionName}
            onChange={handleAccordionChange(sectionName)}
            disabled={!isSectionUnlocked[sectionName]}
            className="mt-8"
        >
            <AccordionSummary
                expandIcon={expanded === sectionName ? <ExpandLessIcon fontSize="large" /> : <AddIcon fontSize="large" />}
                aria-controls={`${sectionName}bh-content`}
                id={`${sectionName}bh-header`}
                classes={{content: "flex justify-between"}}
            >
                <Typography component="span" variant="h2">{title}</Typography>
                {finishedSections[sectionName] && <CheckCircleIcon color="primary" />}
            </AccordionSummary>
            <AccordionDetails>
                {sectionName === SECTIONS.IDENTITY && (
                    <IdentityForm data={studentData} onFinish={onFinishAccordion} />
                )}
                {sectionName === SECTIONS.ENROLLMENT && (
                    <EnrollmentForm data={studentData} onFinish={onFinishAccordion} />
                )}
                {sectionName === SECTIONS.BACKGROUND && (
                    <BackgroundForm data={studentData} onFinish={onFinishAccordion} />
                )}
                {sectionName === SECTIONS.ADDRESS && (
                    <GuardianAddressForm data={studentData} onFinish={onFinishAccordion} isSaving={isPendingStudentsRequest} />
                )}
            </AccordionDetails>
        </Accordion>
    ), [handleAccordionChange, expanded, onFinishAccordion, studentData, finishedSections, isPendingStudentsRequest]);

    return (
        <div className="StudentsPage">
            <div className="StudentsPage__header">
                <Typography variant="h1">
                    {pretitle}
                    {t("students.title")}
                </Typography>
                <Typography variant="body1" className="mt-4" gutterBottom>
                    {t("students.subtitle")}
                </Typography>
                {studentData?.order > 0 ? (
                    <Button
                        variant="text"
                        type="button"
                        onClick={() => setShowRemoveModal(true)}
                        disabled={isPendingStudentsRequest}
                        className="StudentsPage__remove-btn"
                    >
                        {t("students.removeStudent")}
                    </Button>
                ) : null}
            </div>
            {renderAccordion(SECTIONS.IDENTITY, t("students.identity.title"), true)}
            {renderAccordion(SECTIONS.ENROLLMENT, t("students.enrollment.title"))}
            {renderAccordion(SECTIONS.BACKGROUND, t("students.background.title"), true)}
            {renderAccordion(SECTIONS.ADDRESS, t("students.address.title"))}
            <div className="mt-12">
                <Button
                    variant="back"
                    onClick={handleBack}
                    disabled={!hasStudentCreated}
                    startIcon={<PlayArrowIcon className="rotate-180" fontSize="large"/>}
                >
                    {t("button.back")}
                </Button>
            </div>
            <Modal
                open={showRemoveModal}
                onClose={onModalClose}
                title={t("students.removeStudentModal.title")}
                description={t("students.removeStudentModal.description")}
                secondaryButtonText={t("button.cancel")}
                primaryButtonText={t("students.removeStudentModal.proceed")}
                onSecondaryClick={() => setShowRemoveModal(false)}
                onPrimaryClick={onRemoveStudent}
                disabled={isRemovingStudent}
            />
            <Modal
                open={showModal}
                onClose={onModalClose}
                title={t("students.moreStudentsModal.title")}
                description={t("students.moreStudentsModal.description")}
                secondaryButtonText={t("students.moreStudentsModal.addStudent")}
                primaryButtonText={t("students.moreStudentsModal.proceed")}
                onSecondaryClick={onAddAnotherStudent}
                onPrimaryClick={onProceedNext}
            />
        </div>
    );
};

export default StudentsPage;
